import type { ScheduleTab } from './resolveDefaultScheduleTab'

/**
 * Which schedule tab shows a given instant.
 *
 * Used after a create/update so the list follows the event the user just
 * touched: save an event for next week while reading Past, and Past is no
 * longer where that event lives.
 *
 * This deliberately answers a NARROWER question than
 * `resolveDefaultScheduleTab`. That one picks an opening tab from the whole
 * data set, games first; this one just locates a single date. Re-running the
 * full resolver after a mutation would move the user for reasons unrelated to
 * their edit — following the touched event is both more predictable and more
 * likely to be what they wanted.
 *
 * Returns null when the date is missing or unparseable, which callers treat as
 * "no opinion" and leave the tab alone.
 *
 * The tab windows match `resolveDefaultScheduleTab`: today runs from local
 * midnight to the next local midnight, anything at or after that is upcoming,
 * anything before is past.
 */
export const resolveTabForDate = (
  startsAt: string | null | undefined,
  now: Date = new Date()
): ScheduleTab | null => {
  if (!startsAt) {
    return null
  }

  const time = new Date(startsAt).getTime()
  if (Number.isNaN(time)) {
    return null
  }

  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)
  const startOfTomorrow = new Date(startOfToday)
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1)

  if (time >= startOfTomorrow.getTime()) {
    return 'upcoming'
  }
  if (time >= startOfToday.getTime()) {
    return 'today'
  }
  return 'past'
}
